import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import VideogameAssetRoundedIcon from '@mui/icons-material/VideogameAssetRounded';
import dayjs from 'dayjs';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { usePuzzleDay } from '../hooks/usePuzzleDay';
import { useUser } from '../providers/UserProvider';
import { formatPuzzleDate } from '../utils/helpers';
import { HeaderCallout } from './HeaderCallout';

const crossPromotionCalloutKey = 'crossPromotionCallout';
const crossPromotionCalloutVersion = 1;
const crossPromotionStartTime = 1777204800000;
const crossPromotionEndTime = 1778509008000;

const PRANK_REVEALED_STORAGE_KEY = 'flagle-prank-revealed-2026';

type MenuItem = {
  to: string;
  title: string;
  icon: React.ReactNode;
  isNew?: boolean;
  isBeta?: boolean;
  // title is a literal string rather than an i18n key
  isRawTitle?: boolean;
  onClick?: () => void;
  opensInNewTab?: boolean;
  itemClassName?: string;
  itemStyle?: React.CSSProperties;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
};

type MenuSection = {
  /** i18n key for the section heading; omit for an unlabelled group */
  label?: string;
  items: MenuItem[];
  /** standalone "Get Passport" block (rendered outside the normal section padding) */
  passport?: boolean;
};

const PLAY_ITEMS: MenuItem[] = [
  {
    to: '/archive',
    title: 'archive.archive',
    icon: <CalendarMonthRoundedIcon fontSize="small" />,
  },
  {
    to: '/teuteuf-games',
    title: 'other-games',
    icon: <VideogameAssetRoundedIcon fontSize="small" />,
  },
  {
    to: '/city-flags',
    title: 'modes.city-flags.title',
    icon: <LocationCityRoundedIcon fontSize="small" />,
  },
  {
    to: '/emblems',
    title: 'modes.emblems.title',
    icon: <ShieldRoundedIcon fontSize="small" />,
  },
];

const FAQ_ITEM: MenuItem = {
  to: '/faq',
  title: 'FAQ',
  icon: <QuestionAnswerRoundedIcon fontSize="small" />,
};

const ACCOUNT_ITEMS: MenuItem[] = [
  {
    to: '/settings',
    title: 'settings.title',
    icon: <SettingsRoundedIcon fontSize="small" />,
  },
  {
    to: '/account',
    title: 'account.loggedOut.login',
    icon: <PersonRoundedIcon fontSize="small" />,
  },
  {
    to: '#',
    title: 'account.loggedIn.signOut',
    icon: <LogoutRoundedIcon fontSize="small" />,
  },
];

const LEARN_ITEMS: MenuItem[] = [
  {
    to: 'https://edu.teuteuf.fr?utm_source=flagle&utm_medium=menu_link',
    title: 'Teuteuf Edu',
    icon: <SchoolRoundedIcon fontSize="small" />,
    opensInNewTab: true,
    isNew: Date.now() < 1795363649000,
  },
];

const SOCIAL_LINKS: {
  href: string;
  label: string;
  src: string;
  extraClasses?: string;
}[] = [
  {
    href: 'https://www.facebook.com/teuteufgames/',
    label: 'Facebook',
    src: '/images/icons/facebook.png',
  },
  {
    href: 'https://www.tiktok.com/@teuteufgames',
    label: 'TikTok',
    src: '/images/icons/tiktok.svg',
    extraClasses: 'dark:invert',
  },
  {
    href: 'https://www.instagram.com/teuteufgames',
    label: 'Instagram',
    src: '/images/icons/instagram.png',
  },
];

const HamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { userDetails, logout } = useUser();
  const { t, i18n } = useTranslation();
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const { dayString, puzzleNumber } = usePuzzleDay();

  const today = dayjs().format('YYYY-MM-DD');
  const [isPrankRevealed, setIsPrankRevealed] = useState(() => {
    try {
      return localStorage.getItem(PRANK_REVEALED_STORAGE_KEY) === today;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = () => setIsPrankRevealed(true);
    window.addEventListener('prank-revealed', handler);
    return () => window.removeEventListener('prank-revealed', handler);
  }, []);

  useEffect(() => {
    const now = Date.now();
    const storedVersion = Number(
      localStorage.getItem(crossPromotionCalloutKey) ?? '0',
    );
    const isInCampaignWindow =
      now > crossPromotionStartTime && now < crossPromotionEndTime;
    setIsCalloutVisible(
      isInCampaignWindow && storedVersion < crossPromotionCalloutVersion,
    );
  }, []);

  const showGetPassport = !userDetails?.premiumGames?.includes('flagle');

  const menuSections = useMemo<MenuSection[]>(() => {
    const howToPlay: MenuItem = {
      to: isPrankRevealed ? '/' : '/rules',
      title: isPrankRevealed ? 'April Fools' : 'how-to-play.title',
      isRawTitle: isPrankRevealed,
      icon: isPrankRevealed ? (
        <span className="text-lg leading-none">🃏</span>
      ) : (
        <QuestionMarkRoundedIcon fontSize="small" />
      ),
      onClick: isPrankRevealed
        ? () => window.dispatchEvent(new CustomEvent('open-prank-modal'))
        : undefined,
    };

    const sections: MenuSection[] = [
      { label: 'header.menu.play', items: PLAY_ITEMS },
    ];

    if (showGetPassport) {
      sections.push({
        passport: true,
        items: [
          {
            to: '/removeads',
            title: 'header.getPassport',
            icon: (
              <img
                src="/images/icons/teuteuf_passport.png"
                width="18"
                height="18"
                alt=""
                className="drop-shadow"
              />
            ),
          },
        ],
      });
    }

    sections.push(
      { label: 'header.menu.learn', items: LEARN_ITEMS },
      { label: 'header.menu.help', items: [howToPlay, FAQ_ITEM] },
      {
        label: 'account.title',
        items: ACCOUNT_ITEMS.filter(
          (m) =>
            (userDetails.loggedIn || m.to !== '#') &&
            (!userDetails.loggedIn || m.to !== '/account'),
        ),
      },
    );

    return sections;
  }, [userDetails.loggedIn, isPrankRevealed, showGetPassport]);

  // Escape closes the drawer, matching the backdrop click.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Stop the page scrolling underneath the open drawer.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const dismissCallout = () => {
    localStorage.setItem(
      crossPromotionCalloutKey,
      String(crossPromotionCalloutVersion),
    );
    setIsCalloutVisible(false);
  };

  const closeMenu = () => setIsOpen(false);

  return (
    <div ref={menuRef} className="relative">
      {/* Callout */}
      <HeaderCallout anchorRef={menuRef} isOpen={isCalloutVisible}>
        <div className="text-gray-800 relative overflow-hidden">
          <div className="relative z-10 flex flex-col items-start gap-3">
            <div className="flex w-full items-start justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center">
                    <img
                      src="/images/icons/travle.svg"
                      alt="Travle Logo"
                      className="w-5 h-5 mr-1"
                    />
                    travle
                  </h3>
                  <p className="text-sm text-gray-600 text-left">
                    A daily game, get between countries in as few guesses as
                    possible. <span className="font-bold">Recent updates</span>!
                  </p>
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-2">
              <div className="flex-1"></div>
              <button
                onClick={dismissCallout}
                className="px-4 py-2 underline decoration-dotted rounded-lg hover:text-gray-600 transition-colors font-medium text-sm"
              >
                Dismiss
              </button>
              <a
                href="https://travle.earth?utm_source=flagle&utm_medium=header_callout&utm_campaign=cross_promotion"
                target="_blank"
                rel="noopener noreferrer"
                onClick={dismissCallout}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all duration-200 transform hover:scale-[1.02] font-medium text-sm"
              >
                Let&apos;s Go
              </a>
            </div>
          </div>
        </div>
      </HeaderCallout>

      <button
        type="button"
        aria-label={t('header.menu.open')}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className={[
          'flex w-8 h-8 md:w-10 md:h-10 text-2xl md:text-[28px] items-center justify-center rounded-full text-[#333] dark:text-slate-300',
          'transition-colors duration-[5ms] active:duration-0 active:bg-gray-200 dark:active:bg-slate-600',
          'lg:hover:bg-gray-200 lg:dark:hover:bg-slate-600',
          isOpen ? 'bg-gray-200 dark:bg-slate-600' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-center relative">
          <MenuRoundedIcon fontSize="inherit" />
        </div>
      </button>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[10001] bg-black/80 transition-opacity duration-300 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      {/*
        Drawer. `visibility` is transitioned alongside the transform so the
        panel stays out of the tab order while closed, but is not hidden
        mid-animation on the way out.
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('header.menu.title')}
        style={{ height: '100dvh' }}
        className={`fixed top-0 right-0 h-full z-[10002] w-[17rem] bg-white dark:bg-slate-800 shadow-2xl flex flex-col overflow-hidden text-left transition-[transform,visibility] duration-300 ease-in-out ${
          isOpen ? 'translate-x-0 visible' : 'translate-x-full invisible'
        }`}
      >
        <div className="flex items-start justify-between gap-2 px-4 pt-3.5 pb-3 shrink-0 border-b border-gray-100 dark:border-slate-700">
          <div className="min-w-0">
            <Link
              to="/"
              onClick={closeMenu}
              className="flex items-center gap-1.5 text-xl font-bold uppercase leading-none text-[#2E2C2F] dark:text-white"
            >
              <img
                src="/logo512.png"
                alt=""
                aria-hidden="true"
                className="w-6 h-6"
              />
              <span>
                Flag<span style={{ color: '#1a76d2' }}>le</span>
              </span>
            </Link>
            <p className="mt-2 text-[11px] leading-none text-gray-400 dark:text-slate-400 select-none truncate">
              #{puzzleNumber}
              <span aria-hidden="true"> · </span>
              {formatPuzzleDate(dayString, i18n.language)}
            </p>
          </div>
          <button
            type="button"
            onClick={closeMenu}
            aria-label={t('header.menu.close')}
            className="flex w-7 h-7 items-center justify-center rounded-full shrink-0 text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {menuSections.map((section, sectionIndex) =>
            section.passport ? (
              <Fragment key={section.label ?? sectionIndex}>
                {sectionIndex > 0 && (
                  <div className="h-px bg-gray-100 dark:bg-slate-700 mx-4 my-1" />
                )}
                {section.items.map((m, i) => (
                  <PassportMenuButton
                    key={i}
                    to={m.to}
                    title={m.isRawTitle ? m.title : t(m.title)}
                    icon={m.icon}
                    handleClick={() => {
                      if (m.to === '#') {
                        logout();
                      }
                      m.onClick?.();
                      setIsOpen(false);
                    }}
                  />
                ))}
              </Fragment>
            ) : (
              <Fragment key={section.label ?? sectionIndex}>
                {sectionIndex > 0 && (
                  <div className="h-px bg-gray-100 dark:bg-slate-700 mx-4 my-1" />
                )}
                <div className="px-3 pb-2">
                  {section.label && (
                    <p className="px-2 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-slate-400 select-none">
                      {t(section.label)}
                    </p>
                  )}
                  <div className="flex flex-col gap-0.5">
                    {section.items.map((m, i) => (
                      <HamburgerMenuItem
                        key={i}
                        to={m.to}
                        title={m.isRawTitle ? m.title : t(m.title)}
                        icon={m.icon}
                        handleClick={() => {
                          if (m.to === '#') {
                            logout();
                          }
                          m.onClick?.();
                          setIsOpen(false);
                        }}
                        isNew={m.isNew}
                        isBeta={m.isBeta}
                        opensInNewTab={m.opensInNewTab}
                      />
                    ))}
                  </div>
                </div>
              </Fragment>
            ),
          )}
        </div>

        <div className="px-4 pt-3 pb-1 text-center shrink-0 border-t border-gray-100 dark:border-slate-700">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.admiral?.('show', 'cmp.settings');
            }}
            className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 underline transition-colors"
          >
            Privacy Preferences
          </a>
        </div>

        <HamburgerSocialLinks handleClick={closeMenu} />
      </div>
    </div>
  );
};

const PassportMenuButton = ({
  to,
  title,
  icon,
  handleClick,
}: {
  to: string;
  title: string;
  icon: React.ReactNode;
  handleClick: () => void;
}) => {
  return (
    <div className="px-3 pb-1">
      <Link
        to={to}
        onClick={handleClick}
        title={title}
        className="passport-cta relative flex items-center gap-3 overflow-hidden rounded-xl px-3 py-2.5 text-sm font-bold transition-transform duration-200 hover:-translate-y-0.5 hover:scale-[1.02]"
        style={{
          color: '#2a1c00',
          background:
            'linear-gradient(135deg, #fcd34d 0%, #d9a521 50%, #c7a443 100%)',
          boxShadow: '0 4px 16px rgba(212,160,23,0.45)',
        }}
      >
        <span
          className="flex w-8 h-8 items-center justify-center rounded-lg shrink-0"
          style={{
            background: 'linear-gradient(#fef3c7 0%, #fde68a 100%)',
            animation: 'passportIconPulse 2.4s ease-in-out infinite',
          }}
        >
          {icon}
        </span>
        <span className="flex-1 whitespace-nowrap">{title}</span>
        <span className="ml-auto rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-white">
          No ads!
        </span>
      </Link>
    </div>
  );
};

const HamburgerMenuItem = ({
  to,
  title,
  icon,
  handleClick,
  isNew = false,
  isBeta = false,
  opensInNewTab = false,
  itemClassName = '',
  itemStyle,
  iconClassName = '',
  iconStyle,
}: {
  to: string;
  title: string;
  icon: React.ReactNode;
  handleClick: () => void;
  isNew?: boolean;
  isBeta?: boolean;
  opensInNewTab?: boolean;
  itemClassName?: string;
  itemStyle?: React.CSSProperties;
  iconClassName?: string;
  iconStyle?: React.CSSProperties;
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isExternal = /^https?:\/\//i.test(to);
  const isActive = !isExternal && to !== '#' && pathname === to.split('?')[0];
  const linkProps = {
    onClick: handleClick,
    title,
    target: opensInNewTab ? '_blank' : undefined,
    rel: opensInNewTab ? 'noopener noreferrer' : undefined,
    className: `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
      itemClassName ||
      (isActive
        ? 'bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-300'
        : 'text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700/70 active:bg-gray-200 dark:active:bg-slate-700')
    }`,
    style: itemStyle,
  };
  const content = (
    <>
      <span
        className={`flex w-8 h-8 items-center justify-center rounded-lg transition-colors shrink-0 ${
          iconClassName ||
          (isActive
            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
            : 'bg-gray-100 dark:bg-slate-700 text-[#1a76d2] dark:text-blue-400 group-hover:bg-blue-50 group-hover:text-blue-600 dark:group-hover:bg-blue-900/30 dark:group-hover:text-blue-300')
        }`}
        style={iconStyle}
      >
        {icon}
      </span>
      <span className="flex-1 whitespace-nowrap">{title}</span>
      {isNew && (
        <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-red-500 rounded-full px-1.5 py-0.5 leading-none">
          {t('header.new')}
        </span>
      )}
      {isBeta && (
        <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-purple-500 rounded-full px-1.5 py-0.5 leading-none">
          {t('header.beta')}
        </span>
      )}
    </>
  );

  return isExternal ? (
    <a href={to} {...linkProps}>
      {content}
    </a>
  ) : (
    <Link to={to} {...linkProps}>
      {content}
    </Link>
  );
};

const HamburgerSocialLinks = ({ handleClick }: { handleClick: () => void }) => {
  return (
    <div className="flex px-4 py-3 gap-2 justify-center items-center shrink-0">
      {SOCIAL_LINKS.map(({ href, label, src, extraClasses = '' }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noreferrer"
          title={label}
          onClick={handleClick}
          className="group flex w-8 h-8 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        >
          <img
            src={src}
            width={20}
            height={20}
            alt={label}
            className={`w-5 h-5 grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-[opacity,filter] ${extraClasses}`}
          />
        </a>
      ))}
    </div>
  );
};

const HeaderButton = ({
  to,
  title,
  icon,
  onClick,
  extraClasses = '',
}: {
  to: string;
  title: string;
  icon: React.ReactNode;
  onClick?: () => void;
  extraClasses?: string;
}) => {
  return (
    <Link
      to={to}
      title={title}
      onClick={onClick}
      className={[
        'flex w-8 h-8 md:w-10 md:h-10 text-2xl md:text-[28px] items-center justify-center rounded-full text-[#333] dark:text-slate-300',
        // mobile
        'transition-colors duration-[5ms] active:duration-0 active:bg-gray-200 dark:active:bg-slate-600',
        // desktop
        'lg:hover:bg-gray-200 lg:dark:hover:bg-slate-600',
        extraClasses,
      ].join(' ')}
    >
      {icon}
    </Link>
  );
};

const Header = () => {
  const { userDetails } = useUser();
  const { t } = useTranslation();

  const [photoError, setPhotoError] = useState(false);

  const isPremiumUser = userDetails.premiumGames?.includes('flagle');

  const profileIcon = userDetails.loggedIn ? (
    !photoError && userDetails.isPhotoValid ? (
      <img
        src={userDetails.photoURL}
        alt="User"
        className="w-6 h-6 md:w-7 md:h-7 rounded-full object-cover"
        onError={() => setPhotoError(true)}
      />
    ) : (
      <div className="flex w-6 h-6 md:w-7 md:h-7 text-base md:text-xl bg-[#279536] text-white rounded-full items-center justify-center">
        <PersonRoundedIcon fontSize="inherit" />
      </div>
    )
  ) : (
    <div className="flex items-center justify-center relative">
      <PersonRoundedIcon fontSize="inherit" />
      <div className="absolute -bottom-0.5 md:bottom-0 -right-0.5 md:right-0 flex w-3 h-3 bg-red-500 rounded-full items-center justify-center text-[10px] text-white">
        <CloseRoundedIcon fontSize="inherit" />
      </div>
    </div>
  );

  return (
    <header className="flex w-full px-1 md:px-0 py-1 border-b-2 border-[#e5e7eb] dark:border-slate-600 justify-center items-center">
      <div className="flex w-full gap-2.5 items-center justify-start mr-2">
        <HeaderButton
          to="/account"
          title={t('account.title')}
          icon={
            isPremiumUser && userDetails.loggedIn ? (
              <span className="relative flex w-7 h-7 md:w-8 md:h-8 p-0 border-2 border-[#f4d477] rounded-full bg-transparent shadow-[0_0_5px_rgba(212,160,23,0.24)] items-center justify-center">
                <span className="flex w-6 h-6 md:w-7 md:h-7 items-center justify-center overflow-hidden border-2 border-[--light-bg-primary,#fff] dark:border-[--dark-bg-primary,#0f172a] rounded-full">
                  {profileIcon}
                </span>
              </span>
            ) : (
              profileIcon
            )
          }
        />
      </div>

      <Link
        to="/"
        className="text-2xl md:text-3xl font-bold uppercase justify-center items-center text-center leading-none text-[#2E2C2F] dark:text-[#fff]"
      >
        <h1 className="flex flex-row justify-center items-center">
          <img
            src="/logo512.png"
            alt="Flagle Logo"
            className="mr-1 w-7 md:w-6"
          />
          Flag<span style={{ color: '#1a76d2' }}>le</span>
        </h1>
      </Link>

      <div className="flex w-full gap-2.5 items-center justify-end ml-2">
        <HeaderButton
          to="/stats"
          title={t('stats.title')}
          icon={<BarChartRoundedIcon fontSize="inherit" />}
        />
        <HamburgerMenu />
      </div>
    </header>
  );
};

export default Header;
